import { useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';

interface OEmbedPreview {
  provider: string;
  title: string;
  thumbnailUrl: string | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  authorName: string | null;
}

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

export function AddEmbedModal({ onClose, onSaved }: Props) {
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<OEmbedPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const qc = useQueryClient();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setPreview(null);
    setError(null);
    if (!url.trim() || url.length > 2048) return;

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.post('/api/v1/media/embed/preview', { url: url.trim() });
        const data = res.data as Record<string, unknown>;
        if (!data || data.error) {
          setError((data?.error as string) ?? 'Errore nel recupero embed');
        } else {
          setPreview(data as unknown as OEmbedPreview);
        }
      } catch {
        setError('Errore di rete');
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [url]);

  async function handleSave() {
    if (!url.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/v1/media/embed', { url: url.trim() });
      await qc.invalidateQueries({ queryKey: ['media-assets'] });
      onSaved();
    } catch {
      setError('Errore di rete');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 w-full max-w-md shadow-2xl">
        <div className="p-5 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-white">Aggiungi embed esterno</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">&times;</button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-xs text-gray-400">
            Supporta YouTube, Vimeo, SoundCloud, Spotify, Loom, Twitter/X e qualsiasi sito con oEmbed.
          </p>

          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
          />

          {loading && (
            <p className="text-xs text-gray-400">Recupero metadati...</p>
          )}

          {error && (
            <p className="text-xs text-red-400">{error}</p>
          )}

          {preview && (
            <div className="flex gap-3 items-start bg-gray-800 rounded-lg p-3">
              {preview.thumbnailUrl && (
                <img
                  src={preview.thumbnailUrl}
                  alt=""
                  className="w-16 h-12 object-cover rounded flex-shrink-0"
                />
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{preview.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {preview.provider.toUpperCase()}
                  {preview.authorName && ` · ${preview.authorName}`}
                  {preview.duration && ` · ${Math.floor(preview.duration / 60)}:${String(preview.duration % 60).padStart(2, '0')}`}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-700 flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-800"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={!url.trim() || saving}
            className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg"
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}
